import type { ClientLog as ClientLogV3 } from '../proto/v3/client_log';
import type { ClientLog as ClientLogV2} from '../proto/v2/client_log';
import type { CommonPackage } from './common';
import type { CommonConfig } from '@/types/configuration';
import type { SendCallback, LoggerSender } from '@/config/types';
import {
    buildUrl,
    formatUrlQuery,
    Apis,
    Hosts,
    setAvailableHost
} from '@/config/apis';
import ssrGlobal from '@/util/ssr-global';

export type LogPackage = (ClientLogV3.ReportPackage | ClientLogV2.ReportPackage) & { seqid?: number };
export type ReportEvent = ClientLogV3.ReportEvent | ClientLogV2.ReportEvent;

export class BatchLog {
    // 立即发送队列
    asyncQueue: ReportEvent[] = [];
    // 存储待发送的消息
    throttleQueue: ReportEvent[] = [];
    // 存储失败或者超时的数据
    errorQueue: LogPackage[] = [];
    sendingQueue: {[key: string]: LogPackage} = {};
    // 延迟发送定时器
    protected batchWaitTimer: any;
    // 延时补报日志定时器
    private compensateTimer: any;
    // 上一次处理域名兜底网络错误时间
    lastNetworkErrorTimestamp: number = Date.now();
    // 域名兜底网络错误次数
    networkTimes: number = 0;
    commonPackage: CommonPackage;
    config!: CommonConfig;
    url: string = '';
    isV2 = false;
    isDebug = false;
    radarUrl: string = '';
    drained: boolean = false; // 是否未排干日志队列
    baseSendData!: LoggerSender;
    batchCount: number = 50;
    isSetSamplingResult = false;
    // 有值时阻塞日志发送
    sendingYield?: Promise<any> | null = null;
    constructor(config: any, commonPackage: CommonPackage) {
        this.config = config;
        if (this.config.maxBatchLength && this.config.maxBatchLength > 1) {
            this.batchCount = Math.min(50, this.config.maxBatchLength);
        }
        this.commonPackage = commonPackage;
        this.isDebug = this.config.logger || this.config.env === 'logger';
        this.isV2 = this.config.proto === 'v2';
        console.log('config', this.config);
        this.updateUrls();
    }
    sendData(data: {url: string, data: any, [key: string]: any}, callback?: SendCallback) {
        if (typeof this.config.sender === 'function') {
            return this.config.sender(data, callback);
        }
        return this.baseSendData(data, callback);
    }
    get responseSamplingStorageKey() {
        const { product_name, product } = this.commonPackage.app_package;
        const { device_id } = this.commonPackage.identity_package;
        // tslint:disable-next-line:max-line-length
        return `RESPONSE_SAMPLING_STORAGE_KEY_${product_name || product}_${device_id}`;
    }

    /**
     * 根据配置生成上报的 url 地址
     */
    updateUrls() {
        const { env } = this.config;
        // 线上地址 -> 抓包查找工具后端，用以测试后端
        // 兼容方式：若小程序里有扫码工具，扫码后直接进行切换
        if (env && /^(https?:)?\/\//.test(env)) {
            this.url = env;
        } else {
            this.url = buildUrl(env);
        }
        this.formatUrl();
    }

    formatUrl() {
        if (!this.radarUrl) {
            this.radarUrl = this.url.replace(this.url.indexOf(Apis.v2) !== -1 ? Apis.v2 : Apis.v3, Apis.radar);
            // v2 域名不能上报雷达事件
            if (this.radarUrl.indexOf(Hosts.v2.production || '') !== -1) {
                this.radarUrl.replace(Hosts.v2.production || '', Hosts.v3.production);
            }
        }
        const { product_name, product } = this.commonPackage.app_package;
        const querys = `v=__VERSION__&kpn=${product_name || product}`;
        this.url = formatUrlQuery(this.url, querys);
        this.radarUrl = formatUrlQuery(this.radarUrl, querys);
    }
    /**
     * 之所以使用 JSON 而不是 commonPackage 对象，是因为对象可能会变化，而 JSON 则是当时的一个快照，一旦生成不会变化
     */
    getCommonPackageJSON() {
        return this.commonPackage.toJSON();
    }
    /**
     * 进入发送流程
     * @param data
     * @param immediate
     * @param callback 用户自定义 callback
     * @returns
     */
    send(data: ReportEvent, immediate = false, callback?: SendCallback) {
        // 有回调的立即上报
        if (callback || this.drained) {
            return this.sendLogs([data], callback);
        }
        if (immediate) {
            this.sendAsync(data, callback);
            return;
        }
        this.sendThrottle(data);
    }
    /**
     * 短时异步上报
     */
    async sendAsync(data: ReportEvent, callback?: SendCallback) {
        this.asyncQueue.push(data);
        if (this.asyncQueue.length >= this.batchCount) {
            this.flush();
            return;
        }
        // 用一个微任务包裹，减少同时触发的 sendImmediately 对性能影响，TODO：丢失率影响
        if (this.sendingYield) {
            await this.sendingYield;
            this.sendingYield = null;
        }
        try {
            await Promise.resolve();
        } catch (err) {}
        if (!this.asyncQueue.length) return;
        this.flush();
    }
    /**
     * 节流上报
     */
    sendThrottle(data: ReportEvent) {
        this.throttleQueue.push(data);
        // 积攒超过最大
        if (this.throttleQueue.length >= this.batchCount) {
            this.flush();
            return;
        }

        // debounce逻辑
        clearTimeout(this.batchWaitTimer);
        this.batchWaitTimer = setTimeout(() => {
            this.flush();
        }, this.config.wait);
    }
    /**
     * 批量发送日志
     * @param logs
     * @param callback 用户自定义 callback
     */
    sendLogs(logs: ReportEvent[], callback?: SendCallback): void | Boolean {
        if (!logs || !logs.length) {
            return typeof callback === 'function' && callback();
        }
        const data: LogPackage = this.buildLogPackage(logs, this.url);
        // 网络发送错误回调
        const callbackFn = (error?: any) => {
            // 如果网络异常，日志发送失败，则将错误日志缓存，在网络恢复时清空一次错误队列
            if (error) {
                this.errHandler(data);
            } else {
                this.flushErrorLogs();
            }
            typeof callback === 'function' && callback(error);
        }
        this.sendPackage(data, callbackFn);
    }
    /**
     * 将日志包通过接口发送
     * @param data
     * @param callback
     */
    sendPackage(data: LogPackage, callback?: SendCallback) {
        const { timeout } = this.config;
        try {
            // 如果业务方配置了 sender
            this.sendData({
                ...data,
                isDebug: this.isDebug,
                isDrained: this.drained,
                timeout
            }, callback);
        } catch (err) {
            typeof callback === 'function' && callback(err);
        }
    }

    /**
     * 每个 logPackage 包含了所有上报所需的信息，上报地址和上报数据
     * @param logs
     * @param url
     */
    buildLogPackage(logs: ReportEvent[], url: string) {
        if (this.isV2) {
            return this.buildV2Package(logs as ClientLogV2.ReportEvent[], url);
        }
        return this.buildV3Package(logs as ClientLogV3.ReportEvent[], url);
    }
    buildV2Package(logs: ClientLogV2.ReportEvent[], url: string): ClientLogV2.ReportPackage {
        return {
            url,
            data: {
                log: {
                    event: logs
                }
            }
        }
    }
    buildV3Package(logs: ClientLogV3.ReportEvent[], url: string, commonPackageOptions?: {[key: string]: any}): ClientLogV3.ReportPackage {
        const common = this.getCommonPackageJSON();
        commonPackageOptions && Object.assign(common, commonPackageOptions);
        return {
            url,
            data: {
                common,
                logs
            }
        }
    }
    /**
     * 清空当前日志队列
     * @param callback
     */
    flush = (callback?: SendCallback) => {
        this.sendLogs(this.throttleQueue.concat(this.asyncQueue), callback);
        this.throttleQueue = [];
        this.asyncQueue = [];
    }
    /**
     * 页面关闭前排干所有未发送的和发送失败的日志
     */
    drain = () => {
        this.drained = true;
        this.flush();
        this.flushErrorLogs();
        // 处理 beforunload 中断，实际页面未退出的情况
        setTimeout(() => {
            this.drained = false;
        }, 1000);
    }

    /**
     * 日志上报出错处理，一般为网络异常
     * @param logPackage
     */
    async errHandler(logPackage: LogPackage) {
        if (this.isV2) {
            this.networkTimes++;
            this.errorQueue.unshift(logPackage);
            if (
                this.config.env === 'production'
                && ssrGlobal.XMLHttpRequest
                && (this.lastNetworkErrorTimestamp + 1800000 < Date.now() || this.networkTimes >= 5)
            ) {
                this.lastNetworkErrorTimestamp = Date.now();
                this.networkTimes = 0;
                await setAvailableHost(this.config.proto);
                this.updateUrls();
            }
            return;
        }
        const { data } = logPackage as ClientLogV3.ReportPackage;
        if (!data.logs.length) return;
        // http_seq_id 和 client_timestamp 不是必须的，合并时删除
        delete data.common.h5_extra_attr.http_seq_id;
        delete data.common.h5_extra_attr.client_timestamp;

        // 尝试合并相同 url 和 common 的请求
        let merged = false;
        for (let i = 0; i < this.errorQueue.length; i++) {
            const currentLog = this.errorQueue[i]  as ClientLogV3.ReportPackage;
            if (
                currentLog.url === logPackage.url &&
                currentLog.data.logs.length + data.logs.length <= 100 &&
                JSON.stringify(currentLog.data.common) === JSON.stringify(data.common)
            ) {
                merged = true;
                currentLog.data.logs.push(...data.logs);
                break;
            }
        }
        if (!merged) {
            // 先进后出
            // 最大错误数量限制为 5
            if (this.errorQueue.length >= 5) {
                this.errorQueue.pop();
            }
            this.errorQueue.unshift(logPackage);
        }
    }

    // 当网络恢复时尝试一次上报
    flushErrorLogs() {
        this.errorQueue.forEach(logPackage => {
            this.sendPackage({ ...logPackage, url: this.url });
        });
        this.errorQueue = [];
    }

    destory() {
        this.batchWaitTimer && clearTimeout(this.batchWaitTimer);
        this.compensateTimer && clearTimeout(this.compensateTimer);
    }
    /**
     * 雷达专用上报（雷达上报接口和其他事件上报接口做了区分，用于分流统计）
     * @param radar
     */
    sendRadar(radar: ClientLogV3.ReportEvent, service_name?: string) {
        const options = service_name ? { service_name } : undefined;
        const radarPackage = this.buildV3Package([radar], this.radarUrl || this.url, options);
        this.sendPackage(radarPackage);
    }
}
